import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fetchWorkVideos } from '../services/youtube';
import type { YouTubeVideo } from '../services/youtube';

export default function Work() {
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);

  useEffect(() => {
    const loadVideos = async () => {
      try {
        const workVideos = await fetchWorkVideos();
        setVideos(workVideos);
      } catch (error) {
        console.error('Error loading videos:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadVideos();
  }, []);

  const handleVideoClick = (videoId: string) => {
    if (playingVideo === videoId) {
      setPlayingVideo(null);
    } else {
      setPlayingVideo(videoId);
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', color: 'white' }}>

      {/* BG video */}
      <div className="fixed inset-0 z-0 bg-black">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full"
          style={{ objectFit: 'cover' }}
        >
          <source src="https://res.cloudinary.com/ddsnj941k/video/upload/hero_yiyrov.mp4" type="video/mp4" />
        </video>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.82)', backdropFilter: 'blur(1px)' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 px-6 md:px-16 py-28 max-w-6xl mx-auto">

        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <span className="text-xs tracking-[0.4em] uppercase mb-3 block" style={{ color: '#0be3c5' }}>
            PORTFOLIO
          </span>
          <h1 className="text-5xl md:text-7xl font-black leading-tight mb-4">
            ALL WORK
          </h1>
          <p className="text-white/50 text-lg max-w-xl mx-auto">
            A collection of cinematic edits, brand films, and creative projects. Click any video to watch inline.
          </p>
        </motion.div>

        {/* Portfolio Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="text-white/40 animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-white/40">No videos found. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {videos.map((video, index) => (
              <VideoCard
                key={video.id}
                video={video}
                playingVideo={playingVideo}
                onVideoClick={handleVideoClick}
                index={index}
              />
            ))}
          </div>
        )}

        {/* Info Section */}
        <motion.div
          className="text-center max-w-2xl mx-auto mt-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Like What You See?</h2>
          <p className="text-white/60 mb-8">
            These videos are automatically updated from my YouTube channel. 
            Let's create something amazing together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-black text-base transition-all duration-300 hover:opacity-90 hover:scale-[1.02]"
              style={{ background: '#0be3c5', boxShadow: '0 0 24px rgba(11,227,197,0.25)' }}
            >
              Start a Project
            </a>
            <a 
              href="https://www.youtube.com/@YourChannel" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-bold text-base transition-all duration-300 hover:opacity-90 hover:scale-[1.02]"
              style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              Subscribe on YouTube
            </a>
          </div>
        </motion.div>

        <p className="text-center text-white/20 text-xs mt-12 tracking-wide">
          Portfolio automatically synced from YouTube channel.
        </p>
      </div>
    </div>
  );
}

function VideoCard({ video, playingVideo, onVideoClick, index }: {
  video: YouTubeVideo;
  playingVideo: string | null;
  onVideoClick: (videoId: string) => void;
  index: number;
}) {
  return (
    <motion.div
      className="rounded-2xl border overflow-hidden"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(14px)', borderColor: 'rgba(255,255,255,0.08)' }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: 'easeOut' }}
    >
      {playingVideo === video.videoId ? (
        // Inline YouTube Player
        <div className="relative bg-black group" style={{ aspectRatio: '16/9' }}>
          <iframe
            src={`${video.embedUrl}&autoplay=1`}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
          />
          <button
            onClick={() => onVideoClick('')}
            className="absolute top-4 right-4 p-2 bg-black/60 hover:bg-black/80 rounded-lg backdrop-blur-sm transition-colors z-10"
            title="Close video"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              className="text-white"
            >
              <path
                d="M12 4L4 12M4 4L12 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      ) : (
        // Video thumbnail
        <div 
          className="relative bg-black group cursor-pointer" 
          style={{ aspectRatio: '16/9' }}
          onClick={() => onVideoClick(video.videoId)}
        >
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          
          {/* Play Button - Styled like the image */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center transition-all duration-300 hover:bg-black/80 hover:scale-110">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="white"
                className="ml-1"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>

          {/* gradient overlay with title */}
          <div className="absolute bottom-0 left-0 right-0 px-6 pb-5 pt-12" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85), transparent)' }}>
            <span className="text-xs tracking-[0.35em] uppercase" style={{ color: '#0be3c5' }}>Video</span>
            <h3 className="text-white font-black text-xl leading-tight line-clamp-2">{video.title}</h3>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="p-7">
        <p className="text-white/50 text-base leading-relaxed">
          {new Date(video.publishedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
          })}
        </p>
      </div>
    </motion.div>
  );
}
